import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BladeLayout from '../components/BladeLayout';
import menuData from '../menu.json';
import AboutUsContent from '../components/MenuContent/AboutUsContent';
import EditorialBoardContent from '../components/MenuContent/EditorialBoardContent';
import IssuesContent from '../components/MenuContent/IssuesContent';
import GuidelinesContent from '../components/MenuContent/GuidelinesContent';
import SubmissionContent from '../components/MenuContent/SubmissionContent';
import ContactUsContent from '../components/MenuContent/ContactUsContent';

const contentComponents = {
  'About Us': AboutUsContent,
  'Editorial Board': EditorialBoardContent,
  'Issues': IssuesContent,
  'Guidelines for Authors': GuidelinesContent,
  'Submission': SubmissionContent,
  'Contact Us': ContactUsContent,
};

export default function DynamicBladePage() {
  const { bladeKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize the lookup to avoid dependency array issues
  const { foundMain, foundSub, isSingle } = useMemo(() => {
    let foundMain = null, foundSub = null, isSingle = false;
    
    // First check for direct matches in menu data
    for (const main in menuData) {
      if (typeof menuData[main] === 'string' && menuData[main] === bladeKey) {
        foundMain = main;
        isSingle = true;
        break;
      }
      
      // Then check submenu items
      if (typeof menuData[main] === 'object') {
        for (const sub in menuData[main]) {
          if (menuData[main][sub] === bladeKey) {
            foundMain = main;
            foundSub = sub;
            break;
          }
        }
        if (foundMain) break;
      }
    }

    // Default to first main/sub if not found
    if (!foundMain) {
      foundMain = Object.keys(menuData)[0];
      const value = menuData[foundMain];
      if (typeof value === 'string') {
        isSingle = true;
      } else {
        foundSub = Object.keys(value)[0];
      }
    }

    return { foundMain, foundSub, isSingle };
  }, [bladeKey]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Handle redirection for parent menu items
  useEffect(() => {
    if (!isSingle && foundMain && !foundSub && typeof menuData[foundMain] === 'object') {
      const firstSubKey = Object.keys(menuData[foundMain])[0];
      const firstSubValue = menuData[foundMain][firstSubKey];
      const normalizedPath = firstSubValue.startsWith('/') ? firstSubValue : `/${firstSubValue}`;
      // Check if the current path matches the main menu item pattern
      const mainMenuPath = foundMain.toLowerCase().replace(/\s+/g, '-');
      if (bladeKey === mainMenuPath || location.pathname === `/${mainMenuPath}`) {
        navigate(normalizedPath, { replace: true });
      }
    }
  }, [isSingle, foundMain, foundSub, bladeKey, navigate, location.pathname]);

  // When user clicks submenu, update URL
  const handleSubMenuClick = (sub) => {
    const subValue = menuData[foundMain][sub];
    const normalizedPath = subValue.startsWith('/') ? subValue : `/${subValue}`;
    navigate(normalizedPath);
  };

  const subMenu = menuData[foundMain];
  const ContentComponent = contentComponents[foundMain];

  // Always use BladeLayout, but with different menu configurations
  return (
    <div className="animate-fadein" key={bladeKey}>
      <BladeLayout
        menu={isSingle ? null : subMenu}
        selectedKey={foundSub}
        onSelect={handleSubMenuClick}
        title={foundMain}
      >
        {ContentComponent ? (
          <ContentComponent submenuKey={!isSingle ? menuData[foundMain][foundSub] : undefined} />
        ) : (
          <div className="animate-fadein">
            <h1>{foundSub || foundMain}</h1>
            <p>No content yet.</p>
          </div>
        )}
      </BladeLayout>
    </div>
  );
} 